import { useContext, useEffect, useState, useRef } from 'react';
import { AxiosResponse, AxiosError } from 'axios';

import { TargetStatus, Target, TargetRequest, tracking, abortTracking } from '@/tmp';
import { ctxPluto } from '@/contexts/PlutoContext';
import { ctxPopup } from '@/contexts/PopupContext';
import { rethrowIfNotClientException } from '@/utilities/throwableUtils';
import { MinimalTarget } from '@/generated/model';

// TODO localstorage-bol inditani, oda menteni
// leiratkozni a trackingbol ???

function getIds(targets: MinimalTarget[] | undefined) {
  if (!targets || !targets.length) return undefined;
  return targets.map((t) => t.id);
}

// maps targetid list to server expected structure
function getTargetRequests(targets: number[], asynchronous: boolean) {
  return targets.map((id) => {
    return {
      targetId: id,
      fromTimeMicros: -1,
      asynchronous,
    } as TargetRequest;
  });
}

function showResponse(response: AxiosResponse<Target[] | undefined, unknown>) {
  try {
    return JSON.stringify(response);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e) {
    return 'Can not show response!';
  }
}

function arrayCompare(a: number[] | undefined, b: number[] | undefined) {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  if (a.length !== b.length) return false;
  // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
  return JSON.stringify(a.sort()) === JSON.stringify(b.sort());
}

export function useTargetTracking(): Target[] | undefined {
  const ctx = useContext(ctxPluto);
  const ctxPopups = useContext(ctxPopup);
  const [value, setValue] = useState<Target[] | undefined>(undefined);
  const timeoutId = useRef<NodeJS.Timeout | undefined>(undefined);

  const [targetIdsCache, setTargetIdsCache] = useState<number[] | undefined>(undefined); // ez nem kellene
  const [popupTargetIdCache, setPopupTargetIdCache] = useState<number | undefined>(undefined); // ez nem kellene
  const [pathLengthMinutes, setPathLengthMinutes] = useState<number | undefined>(undefined);

  useEffect(() => {
    async function loadTargets(targetIds: number[], asynchronous: boolean) {
      if (!asynchronous) {
        ctxPopups?.showLoader();
      }
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = undefined;
      }
      if (!targetIds.length) {
        await abortTracking();
        setValue(undefined);
        return;
      }

      try {
        const response = await tracking(
          getTargetRequests(targetIds, asynchronous),
          ctx?.popupTargetId,
          ctx?.pathLengthMinutes
        );
        if (response.status === 200) {
          if (!response.data || !(response.data instanceof Array)) {
            setValue(undefined);
            console.log('server returned: ', showResponse(response));
            rescheduleTracking();
            return;
          } else if (!response.data.length) {
            setValue(undefined);
          } else {
            setValue(response.data);
          }
          removeSelectedTargets(response.data);
          const newStatusMap = new Map<number, TargetStatus>(ctx?.selectedTargetStatus);
          response.data!.forEach((ft) => {
            newStatusMap.set(ft.target.id, ft.currentPosition ? 'validstatus' : 'nostatus');
          });
          ctx!.setSelectedTargetStatus(newStatusMap);
          void loadTargets(getIds(ctx?.selectedTargets)!, true);
        } else {
          // FIXME jobb hibakezeles kellene
          // 499 = cancelled
          if (response.status !== 499) {
            rescheduleTracking();
          }
        }
      } catch (error) {
        rescheduleTracking();
        if (
          !(error instanceof AxiosError && error.code === 'ECONNABORTED' && error.message.includes('Request aborted'))
        ) {
          rethrowIfNotClientException(error);
        }
      } finally {
        if (!asynchronous) {
          ctxPopups?.hideLoader();
        }
      }
    }

    function refresh(targetIds: number[] | undefined) {
      if (targetIds) {
        setTargetIdsCache(targetIds);
        setPopupTargetIdCache(ctx?.popupTargetId);
        void loadTargets(targetIds, false); // get immediate response
      }
    }

    const targetIds = getIds(ctx?.selectedTargets);

    if (ctx?.immediateLoadTargetId) {
      ctx?.setImmediateLoadTargetId(undefined);
      refresh(targetIds);
      return;
    }

    if (pathLengthMinutes != ctx?.pathLengthMinutes) {
      setPathLengthMinutes(ctx?.pathLengthMinutes);
      void refresh(targetIds);
    }

    if (targetIds) {
      // valamiert a target selectorban scrollozas kozben ujra bejon ide, de ugyanazokkal a targetId-kkel
      // ez felesleges szerverhivasokat okoz es ha gyorsan teker akkor a szerver oldali throttling is hibat ad
      // emiatt ezt kiszurjuk
      if (!arrayCompare(targetIds, targetIdsCache) || ctx?.popupTargetId !== popupTargetIdCache) {
        refresh(targetIds);
      }
    } else {
      void abortTracking();
      setTargetIdsCache(undefined);
      setPopupTargetIdCache(undefined);
      setValue(undefined);
    }

    function removeSelectedTargets(trackingResponse: Target[]) {
      if (ctx?.selectedTargets && trackingResponse.length >= ctx?.selectedTargets.length) {
        return;
      }
      const targetIds = trackingResponse.map((t) => t.target.id);
      ctx?.setSelectedTargets((old) => {
        const newArray = old?.filter((target) => targetIds.includes(target.id));
        return newArray;
      });
    }

    window.continueTracking = function () {
      const targetIds = getIds(ctx?.selectedTargets);
      if (targetIds) {
        void loadTargets(targetIds, false); // get immediate response
      }
    };

    function rescheduleTracking() {
      timeoutId.current = setTimeout(() => {
        void loadTargets(getIds(ctx?.selectedTargets)!, true);
      }, 20000);
    }

    // nem akarunk a ctx-re dependalni...
  }, [ctx?.selectedTargets, ctx?.popupTargetId, ctx?.immediateLoadTargetId, ctx?.pathLengthMinutes]);

  return value;
}
